import type { CatalogComponentProperties } from '@/domain/catalog/common';
import { parseInt } from 'lodash';

export enum HeadSystem {
    // noinspection JSUnusedGlobalSymbols
    TYPE1_COCR = 'type1-cocr',
    COCR_1214 = '1214-cocr',
}

export const headSystems = Object.values(HeadSystem);
export type HeadSystemValue = (typeof headSystems)[number];

export function isHeadSystem(value: string): value is HeadSystemValue {
    return headSystems.includes(value as HeadSystemValue);
}

export enum HeadSize {
    // noinspection JSUnusedGlobalSymbols
    H22 = 22,
    H28 = 28,
    H32 = 32,
    H36 = 36,
}

export const headSizes = Object.values(HeadSize).filter((value): value is number => typeof value === 'number');

export type HeadSizeString = `${HeadSize} mm`;
const headSizeRegex = /(\d+) mm/;

export function isHeadSize(value: number): value is HeadSize {
    return headSizes.includes(value as HeadSize);
}

export function headSizeString(size: HeadSize): HeadSizeString {
    return `${size} mm`;
}

export function isHeadSizeString(value: string): value is HeadSizeString {
    return !!value.match(headSizeRegex);
}

export function headSizeFromString(value: string): HeadSize {
    const match = value.match(headSizeRegex);
    if (!match) {
        throw Error(`Invalid head-size string '${value}'`);
    }
    const result = parseInt(match[1]);
    if (!isHeadSize(result)) {
        throw Error(`Invalid head-size ${result}`);
    }
    return result;
}

// Note: All head-types are currently just an empty string
export const headTypes = [''] as const;
export type HeadType = (typeof headTypes)[number];

export function isHeadType(value: string): value is HeadType {
    return headTypes.includes(value as HeadType);
}

// A complete list of all available head offsets in our API catalogue
enum OffsetSize {
    // noinspection JSUnusedGlobalSymbols
    MINUS_6 = -6,
    MINUS_3_5 = -3.5,
    MINUS_3 = -3,
    ZERO = 0,
    PLUS_3 = 3,
    PLUS_3_5 = 3.5,
    PLUS_6 = 6,
    PLUS_7 = 7,
    PLUS_9 = 9, // Unused, but reserved to validate API response
    PLUS_10_5 = 10.5,
    PLUS_12 = 12, // Unused, but reserved to validate API response
}

export enum CocrOffset {
    // noinspection JSUnusedGlobalSymbols
    MINUS_6 = OffsetSize.MINUS_6,
    MINUS_3_5 = OffsetSize.MINUS_3_5,
    ZERO = OffsetSize.ZERO,
    PLUS_3_5 = OffsetSize.PLUS_3_5,
    PLUS_7 = OffsetSize.PLUS_7,
    PLUS_10_5 = OffsetSize.PLUS_10_5,
}

export enum Type1Offset {
    // noinspection JSUnusedGlobalSymbols
    MINUS_6 = OffsetSize.MINUS_6,
    MINUS_3 = OffsetSize.MINUS_3,
    ZERO = OffsetSize.ZERO,
    PLUS_3 = OffsetSize.PLUS_3,
    PLUS_6 = OffsetSize.PLUS_6,
}

export const headOffsets = Object.values(OffsetSize).filter((value): value is number => typeof value === 'number');
export type HeadOffset = (typeof headOffsets)[number];

export const adapterTypeSize = (OffsetSize.MINUS_6).toString();
export const adapterTypeString = '(adapter type if ceramic)';

export const cocrOffsets = Object.values(CocrOffset).filter((value): value is number => typeof value === 'number');
export const type1Offsets = Object.values(Type1Offset).filter((value): value is number => typeof value === 'number');

export const displayableType1Offsets: HeadOffset[] = type1Offsets;
export const displayableCocrOffsets: HeadOffset[] = cocrOffsets;

export function isHeadOffset(value: number): value is HeadOffset {
    return headOffsets.includes(value as HeadOffset);
}

/** Properties of a type of head, independent of its use in any particular case  */
export type CatalogHead = CatalogComponentProperties & {
    /** The system the head belongs to */
    system: HeadSystemValue;

    /** The size of the head */
    size: HeadSizeString;

    /** All head-types are currently just an empty string */
    type: HeadType;

    /** Offset */
    offset: HeadOffset;
};
